import { Controller } from "@hotwired/stimulus";

const FORM_CONFIG: Record<string, { title: string; subtitle: string; placeholder: string }> = {
  bug: {
    title: "Report a bug",
    subtitle: "We typically respond within a few hours",
    placeholder: "What happened? What did you expect to happen?",
  },
  general_help: {
    title: "General help",
    subtitle: "We typically respond within a few hours",
    placeholder: "What do you need help with?",
  },
  dse: {
    title: "Talk to your DSE",
    subtitle: "Your Developer Success Engineer is here to help",
    placeholder: "How can we help with your evaluation?",
  },
};

export default class extends Controller {
  static targets = [
    "surfacePanel",
    "formPanel",
    "formTitle",
    "formSubtitle",
    "supportTypeField",
    "subjectField",
    "messageField",
    "submitButton",
    "toast",
    "actualForm",
    "fileInput",
    "fileList",
    "uploadProgress",
    "attachmentIdsField",
    "dropZone",
  ];

  static values = {
    organizationId: String,
    uploadUrl: String,
  };

  declare readonly organizationIdValue: string;
  declare readonly uploadUrlValue: string;

  declare readonly surfacePanelTarget: HTMLElement;
  declare readonly formPanelTarget: HTMLElement;
  declare readonly formTitleTarget: HTMLElement;
  declare readonly formSubtitleTarget: HTMLElement;
  declare readonly supportTypeFieldTarget: HTMLInputElement;
  declare readonly subjectFieldTarget: HTMLInputElement;
  declare readonly messageFieldTarget: HTMLTextAreaElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly toastTarget: HTMLElement;
  declare readonly actualFormTarget: HTMLFormElement;
  declare readonly fileInputTarget: HTMLInputElement;
  declare readonly fileListTarget: HTMLElement;
  declare readonly uploadProgressTarget: HTMLElement;
  declare readonly attachmentIdsFieldTarget: HTMLInputElement;
  declare readonly dropZoneTarget: HTMLElement;

  private isSubmitting = false;
  private formActive = false;
  private uploadingCount = 0;
  private uploadGeneration = 0;
  private pendingType: string | null = null;
  private pendingSubject: string | null = null;
  private uploadedAttachments: Array<{ id: string; filename: string; size: number }> = [];
  // NOTE: These values mirror AttachmentValidator (app/models/attachment_validator.rb).
  // Update both if limits change. Plain caps email attachments at 6 MB combined.
  private readonly MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB per file
  private readonly MAX_TOTAL_SIZE = 6 * 1024 * 1024; // 6MB combined (Plain email limit)
  private readonly MAX_FILES = 5;
  private boundHandleDrawerToggle: (event: Event) => void = this.handleDrawerToggle.bind(this);
  private boundCaptureTrigger = this.captureTrigger.bind(this);

  connect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.addEventListener("toggle", this.boundHandleDrawerToggle);
    }

    // Capture trigger button data BEFORE the popover steals focus
    document.addEventListener("click", this.boundCaptureTrigger, true);
  }

  disconnect() {
    const drawer = document.getElementById("support-drawer");
    if (drawer) {
      drawer.removeEventListener("toggle", this.boundHandleDrawerToggle);
    }
    document.removeEventListener("click", this.boundCaptureTrigger, true);
  }

  // Capture click on any trigger button before the popover opens
  private captureTrigger(event: Event) {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      '[popovertarget="support-drawer"]',
    );
    if (button) {
      this.pendingType = button.dataset.supportType || null;
      this.pendingSubject = button.dataset.subject || null;
    }
  }

  private handleDrawerToggle(event: Event) {
    const drawer = event.target as HTMLElement & { matches: (selector: string) => boolean };

    if (!drawer.matches(":popover-open")) {
      return;
    }

    // If the trigger specified a type, skip the surface and go straight to that form.
    if (this.pendingType) {
      const type = this.pendingType;
      const subject = this.pendingSubject;
      this.pendingType = null;
      this.pendingSubject = null;

      setTimeout(() => {
        this.resetForm();
        this.transitionToForm(type);
        if (subject) {
          this.subjectFieldTarget.value = subject;
        }
      }, 0);
      return;
    }

    // Otherwise restore whichever panel the user last had open. Closing the drawer by
    // clicking outside only hides the popover (the DOM stays intact), so any in-progress
    // input is preserved until the form is submitted or explicitly cancelled.
    if (this.formActive) {
      this.showFormPanel();
    } else {
      this.showSurfacePanel();
    }
  }

  // Called by card buttons on the surface panel
  showForm(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const type = button.dataset.type || "general_help";
    this.transitionToForm(type);
  }

  // Called by back button and cancel button on the form panel — an explicit abandon,
  // so the in-progress input is cleared (unlike clicking outside to close the drawer).
  showSurface() {
    this.showSurfacePanel();
    this.resetForm();
    this.formActive = false;
  }

  private showSurfacePanel() {
    this.formPanelTarget.classList.add("hidden");
    this.formPanelTarget.classList.remove("flex");
    this.surfacePanelTarget.classList.remove("hidden");
  }

  private showFormPanel() {
    this.surfacePanelTarget.classList.add("hidden");
    this.formPanelTarget.classList.remove("hidden");
    this.formPanelTarget.classList.add("flex");
  }

  private transitionToForm(type: string) {
    const config = FORM_CONFIG[type] || FORM_CONFIG.general_help;

    // Update form header
    this.formTitleTarget.textContent = config.title;
    this.formSubtitleTarget.textContent = config.subtitle;

    // Set hidden field and placeholder
    this.supportTypeFieldTarget.value = type;
    this.messageFieldTarget.placeholder = config.placeholder;

    // Toggle panels
    this.showFormPanel();
    this.formActive = true;
  }

  handleSubmit(event: Event) {
    event.preventDefault();
    if (this.isSubmitting || this.uploadingCount > 0) {
      return;
    }

    const form = event.target as HTMLFormElement;
    this.setSubmitting(true);

    fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        "X-CSRF-Token":
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
        Accept: "application/json",
      },
    })
      .then((response) =>
        response.ok
          ? response.json()
          : response
              .json()
              .catch(() => ({ success: false, error: `Server error: ${response.status}` })),
      )
      .then((data) => this.handleSubmitResponse(data))
      .catch(() => this.showToast("Oops, something went wrong. Please try again.", "error"))
      .finally(() => this.setSubmitting(false));
  }

  private setSubmitting(value: boolean) {
    this.isSubmitting = value;
    this.submitButtonTarget.disabled = value;
    if (value) {
      this.submitButtonTarget.dataset.originalText =
        this.submitButtonTarget.textContent || "Send request";
      this.submitButtonTarget.textContent = "Submitting...";
    } else {
      this.submitButtonTarget.textContent =
        this.submitButtonTarget.dataset.originalText || "Send request";
    }
  }

  private handleSubmitResponse(data: {
    success: boolean;
    message?: string;
    errors?: string[];
    error?: string;
  }) {
    if (data.success) {
      this.showToast(data.message || "Thanks! We'll be in touch shortly.", "success");
      setTimeout(() => {
        const drawer = document.getElementById("support-drawer") as HTMLElement & {
          hidePopover?: () => void;
        };
        drawer?.hidePopover?.();
        this.showSurface();
      }, 2000);
    } else {
      const errorMessage =
        data.errors && Array.isArray(data.errors)
          ? data.errors.join(", ")
          : data.error || "Oops, something went wrong. Please try again.";
      this.showToast(errorMessage, "error");
    }
  }

  private resetForm() {
    this.uploadGeneration += 1;
    this.actualFormTarget.reset();
    this.supportTypeFieldTarget.value = "general_help";
    this.toastTarget.classList.add("hidden");

    // Clear uploaded attachments
    this.uploadedAttachments = [];
    this.updateAttachmentsList();
  }

  // File upload handlers

  async handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    await this.processFiles(files);
    input.value = ""; // Clear input for next selection
  }

  handleDragOver(_dragEvent: DragEvent) {
    this.dropZoneTarget.classList.add("border-lime-500", "bg-lime-50", "text-lime-700");
    this.dropZoneTarget.classList.remove("border-gray-300", "text-charcoal-600");
  }

  handleDragLeave(event: DragEvent) {
    if (this.dropZoneTarget.contains(event.relatedTarget as Node)) {
      return;
    }
    this.resetDropZoneStyle();
  }

  async handleDrop(event: DragEvent) {
    this.resetDropZoneStyle();

    const files = Array.from(event.dataTransfer?.files || []);
    await this.processFiles(files);
  }

  removeAttachment(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const index = parseInt(button.dataset.index || "0", 10);

    this.uploadedAttachments = this.uploadedAttachments.filter(
      (_attachment, attachmentIndex) => attachmentIndex !== index,
    );
    this.updateAttachmentsList();
  }

  showToast(message: string, type: "success" | "error") {
    const toast = this.toastTarget;
    toast.textContent = message;
    toast.classList.remove(
      "bg-green-500",
      "bg-red-500",
      "text-black",
      "text-white",
      "border-l-4",
      "border-green-700",
      "border-red-700",
      "p-4",
      "rounded",
      "hidden",
    );

    toast.classList.add("border-l-4", "p-4", "rounded");

    if (type === "success") {
      toast.classList.add("bg-green-500", "text-black", "border-green-700");
    } else {
      toast.classList.add("bg-red-500", "text-white", "border-red-700");
    }

    setTimeout(() => {
      toast.classList.add("hidden");
    }, 3000);
  }

  private validateFile(file: File): boolean {
    if (file.size > this.MAX_FILE_SIZE) {
      this.showToast(
        `File "${file.name}" is too large (max ${this.MAX_FILE_SIZE / 1024 / 1024}MB)`,
        "error",
      );
      return false;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
      "text/plain",
      "text/csv",
      "application/json",
      "application/zip",
      "application/x-zip-compressed",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(file.type)) {
      this.showToast(`File type "${file.type}" is not allowed`, "error");
      return false;
    }

    return true;
  }

  private async processFiles(files: File[]) {
    if (files.length === 0) {
      return;
    }

    // If a batch is already uploading, uploadedAttachments is stale and the count/size
    // checks below cannot be trusted. Reject the new batch with a clear message.
    if (this.uploadingCount > 0) {
      this.showToast(
        "Please wait for the current upload to finish before adding more files",
        "error",
      );
      return;
    }

    // Filter invalid files first so the count check reflects only the files that would
    // actually be uploaded — avoids rejecting valid files when some in the batch are invalid.
    const validFiles = files.filter((file) => this.validateFile(file));
    if (validFiles.length === 0) {
      return;
    }

    if (this.uploadedAttachments.length + validFiles.length > this.MAX_FILES) {
      this.showToast(`You can only upload up to ${this.MAX_FILES} files`, "error");
      return;
    }

    const currentTotal = this.uploadedAttachments.reduce((sum, att) => sum + att.size, 0);
    const newTotal = validFiles.reduce((sum, file) => sum + file.size, 0);
    if (currentTotal + newTotal > this.MAX_TOTAL_SIZE) {
      this.showToast(
        `Total attachments cannot exceed ${this.MAX_TOTAL_SIZE / 1024 / 1024}MB`,
        "error",
      );
      return;
    }

    await this.uploadFiles(validFiles);
  }

  private async uploadFiles(files: File[]) {
    this.uploadingCount += 1;
    this.submitButtonTarget.disabled = true;
    this.uploadProgressTarget.classList.remove("hidden");

    const generation = this.uploadGeneration;

    for (const file of files) {
      try {
        const completed = await this.uploadFile(file, generation);
        if (!completed) {
          break;
        }
      } catch (error) {
        this.showToast(`Failed to upload ${file.name}`, "error");
      }
    }

    this.uploadingCount -= 1;
    if (this.uploadingCount === 0) {
      this.uploadProgressTarget.classList.add("hidden");
      this.submitButtonTarget.disabled = this.isSubmitting;
    }
  }

  private async uploadFile(file: File, generation: number): Promise<boolean> {
    const uploadUrlData = await this.getUploadUrl(file);
    if (this.uploadGeneration !== generation) {
      return false;
    }

    await this.uploadToS3(file, uploadUrlData);
    if (this.uploadGeneration !== generation) {
      return false;
    }

    this.uploadedAttachments.push({
      id: uploadUrlData.attachment_id,
      filename: file.name,
      size: file.size,
    });

    this.updateAttachmentsList();
    return true;
  }

  private async getUploadUrl(file: File): Promise<{
    attachment_id: string;
    upload_form_url: string;
    upload_form_data?: Array<{ key: string; value: string }>;
  }> {
    const csrfToken =
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to get upload URL");
    }

    return response.json();
  }

  private async uploadToS3(
    file: File,
    uploadData: {
      upload_form_url: string;
      upload_form_data?: Array<{ key: string; value: string }>;
    },
  ) {
    const formData = new FormData();

    if (uploadData.upload_form_data) {
      uploadData.upload_form_data.forEach((field) => {
        formData.append(field.key, field.value);
      });
    }

    formData.append("file", file);

    const response = await fetch(uploadData.upload_form_url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
  }

  private updateAttachmentsList() {
    const ids = this.uploadedAttachments.map((attachment) => attachment.id);
    this.attachmentIdsFieldTarget.value = JSON.stringify(ids);

    this.fileListTarget.replaceChildren();

    if (this.uploadedAttachments.length === 0) {
      this.fileListTarget.classList.add("hidden");
      return;
    }

    this.fileListTarget.classList.remove("hidden");

    for (const [index, attachment] of this.uploadedAttachments.entries()) {
      const row = document.createElement("div");
      row.className =
        "flex items-center justify-between gap-2 p-2 bg-white border border-gray-200 rounded";

      const span = document.createElement("span");
      span.className = "min-w-0 flex-1 truncate text-sm text-gray-900";
      span.textContent = attachment.filename;

      const button = document.createElement("button");
      button.type = "button";
      button.dataset.action = "click->support-widget#removeAttachment";
      button.dataset.index = String(index);
      button.className = "shrink-0 text-gray-800 hover:text-gray-900";
      button.setAttribute("aria-label", `Remove ${attachment.filename}`);
      button.textContent = "×";

      row.append(span, button);
      this.fileListTarget.appendChild(row);
    }
  }

  private resetDropZoneStyle() {
    this.dropZoneTarget.classList.remove("border-lime-500", "bg-lime-50", "text-lime-700");
    this.dropZoneTarget.classList.add("border-gray-300", "text-charcoal-600");
  }
}
